import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Store,
    ArrowRight,
    RefreshCw,
    TrendingUp,
    Users,
    DollarSign,
    AlertCircle,
    Mail,
    User,
    Phone,
    Layout,
    Globe,
    Plus,
    Trash2,
    CheckCircle2
} from 'lucide-react';

export default function Apply() {
    const navigate = useNavigate();
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        displayName: '',
        paymentPhone: '',
        socialMedia: ['']
    });

    const addSocialLink = () => {
        setFormData(prev => ({
            ...prev,
            socialMedia: [...prev.socialMedia, '']
        }));
    };

    const removeSocialLink = (index: number) => {
        if (formData.socialMedia.length === 1) return;
        setFormData(prev => ({
            ...prev,
            socialMedia: prev.socialMedia.filter((_, i) => i !== index)
        }));
    };

    const handleSocialChange = (index: number, value: string) => {
        const newSocial = [...formData.socialMedia];
        newSocial[index] = value;
        setFormData(prev => ({
            ...prev,
            socialMedia: newSocial
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent as keyof typeof prev] as any),
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/auth/register-affiliate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                setError(data.message || 'Failed to submit application');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const benefits = [
        {
            icon: TrendingUp,
            title: 'High Commissions',
            description: 'Earn up to 20% commission on successful referrals.',
        },
        {
            icon: Users,
            title: 'Grow Your Brand',
            description: 'Partner with top brands and broaden your reach.',
        },
        {
            icon: DollarSign,
            title: 'Instant Payouts',
            description: 'Get paid directly to your M-Pesa account.',
        },
    ];

    if (success) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center px-6">
                <div className="max-w-md w-full text-center space-y-8 animate-in fade-in zoom-in duration-700">
                    <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-8">
                        <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    <div className="space-y-4">
                        <h2 className="text-4xl font-light text-black tracking-tight">Application Received</h2>
                        <div className="w-12 h-0.5 bg-black mx-auto"></div>
                        <p className="text-gray-500 font-light leading-relaxed">
                            Thank you for applying to the Dukene Affiliate Program. We've sent a verification email to <span className="font-medium text-black">{formData.email}</span>. Please verify your email to complete your application.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full h-12 bg-black text-white font-bold rounded-xl hover:bg-zinc-900 transition-all uppercase tracking-widest text-xs"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex bg-white">
            {/* Left Side - Info */}
            <div className="hidden lg:flex lg:w-1/2 bg-zinc-50 border-r border-gray-100 relative overflow-hidden">
                <div className="flex flex-col justify-between w-full p-16 xl:p-24 relative z-10">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                            <Store className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
                            <p className="text-xs text-gray-500 font-medium tracking-wide">AFFILIATES</p>
                        </div>
                    </Link>

                    {/* Content */}
                    <div className="space-y-16">
                        <div>
                            <h2 className="text-6xl font-light text-black mb-8 leading-tight tracking-tight">
                                Turn your influence <br />into <span className="font-normal italic">income.</span>
                            </h2>
                            <div className="w-16 h-0.5 bg-black mb-8"></div>
                            <p className="text-xl text-gray-600 leading-relaxed font-light max-w-lg">
                                Join our premium affiliate community today. We provide the tools, products, and tracking you need to succeed.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-12">
                            {benefits.map((benefit, index) => (
                                <div key={index} className="flex items-start gap-6">
                                    <div className="w-12 h-12 border border-black/5 rounded-xl flex items-center justify-center flex-shrink-0 bg-white shadow-sm">
                                        <benefit.icon className="w-5 h-5 text-black" />
                                    </div>
                                    <div>
                                        <h3 className="text-black font-medium text-lg mb-1 tracking-tight">{benefit.title}</h3>
                                        <p className="text-gray-500 font-light leading-relaxed max-w-sm">{benefit.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <p className="text-xs text-gray-400 font-light tracking-wide">
                        © 2024 DUKENE PLATFORM • ALL RIGHTS RESERVED
                    </p>
                </div>

                {/* Subtle Background pattern/gradient */}
                <div className="absolute top-0 right-0 -mr-32 -mt-32 w-96 h-96 bg-zinc-200/50 rounded-full blur-3xl opacity-50"></div>
                <div className="absolute bottom-0 left-0 -ml-32 -mb-32 w-96 h-96 bg-zinc-200/50 rounded-full blur-3xl opacity-50"></div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 overflow-y-auto px-6 py-16 sm:px-12 lg:px-20 xl:px-32">
                <div className="max-w-xl mx-auto">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center gap-3 mb-16">
                        <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                            <Store className="w-5 h-5 text-white" />
                        </div>
                        <h1 className="text-xl font-bold text-black tracking-tight uppercase">Dukene</h1>
                    </div>

                    <div className="mb-12">
                        <h2 className="text-4xl font-light text-black mb-4 tracking-tight">Partner with Us</h2>
                        <p className="text-gray-500 font-light">Fill out the form below to submit your application. Our team will review your profile and get back to you within 48 hours.</p>
                    </div>

                    {error && (
                        <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-600 font-light">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-10">
                        {/* Account Information */}
                        <section className="space-y-6">
                            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest pb-2 border-b border-gray-100">
                                <User className="w-3.5 h-3.5" />
                                <span>Personal Information</span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-black uppercase tracking-widest pl-1">First Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full h-12 bg-zinc-50 border border-transparent focus:border-black focus:bg-white rounded-xl px-4 text-sm font-light transition-all outline-none"
                                        placeholder="John"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-black uppercase tracking-widest pl-1">Last Name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full h-12 bg-zinc-50 border border-transparent focus:border-black focus:bg-white rounded-xl px-4 text-sm font-light transition-all outline-none"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-black uppercase tracking-widest pl-1">Email Address</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full h-12 bg-zinc-50 border border-transparent focus:border-black focus:bg-white rounded-xl pl-12 pr-4 text-sm font-light transition-all outline-none"
                                        placeholder="john@example.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-black uppercase tracking-widest pl-1">WhatsApp Phone Number</label>
                                <div className="relative">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="tel"
                                        name="phoneNumber"
                                        required
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        className="w-full h-12 bg-zinc-50 border border-transparent focus:border-black focus:bg-white rounded-xl pl-12 pr-4 text-sm font-light transition-all outline-none"
                                        placeholder="0712 345 678"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Affiliate Details */}
                        <section className="space-y-6 pt-4">
                            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest pb-2 border-b border-gray-100">
                                <Layout className="w-3.5 h-3.5" />
                                <span>Affiliate Profile</span>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-black uppercase tracking-widest pl-1">Public Display Name</label>
                                <input
                                    type="text"
                                    name="displayName"
                                    required
                                    value={formData.displayName}
                                    onChange={handleChange}
                                    className="w-full h-12 bg-zinc-50 border border-transparent focus:border-black focus:bg-white rounded-xl px-4 text-sm font-light transition-all outline-none"
                                    placeholder="e.g. Trendy Deals Kenya"
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-[11px] font-bold text-black uppercase tracking-widest pl-1 leading-relaxed">
                                    Where do you plan to promote the products?
                                </label>
                                <p className="text-[10px] text-gray-400 font-light pl-1 -mt-2">
                                    Add links to your social media profiles, groups, or platforms.
                                </p>

                                <div className="space-y-3">
                                    {formData.socialMedia.map((link, index) => (
                                        <div key={index} className="flex gap-2">
                                            <div className="relative flex-1">
                                                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                <input
                                                    type="url"
                                                    required
                                                    value={link}
                                                    onChange={(e) => handleSocialChange(index, e.target.value)}
                                                    className="w-full h-10 bg-zinc-50 border border-transparent focus:border-black focus:bg-white rounded-xl pl-12 pr-4 text-xs font-light transition-all outline-none"
                                                    placeholder="https://..."
                                                />
                                            </div>
                                            {formData.socialMedia.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeSocialLink(index)}
                                                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    onClick={addSocialLink}
                                    className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 hover:text-black uppercase tracking-widest pl-1 pt-2 transition-colors"
                                >
                                    <Plus className="w-3 h-3" />
                                    <span>Add another link</span>
                                </button>
                            </div>
                        </section>

                        {/* Payout Details */}
                        <section className="space-y-6 pt-4">
                            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase tracking-widest pb-2 border-b border-gray-100">
                                <DollarSign className="w-3.5 h-3.5" />
                                <span>Payout Information</span>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-black uppercase tracking-widest pl-1">M-Pesa Number for Payouts</label>
                                <div className="relative">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="tel"
                                        name="paymentPhone"
                                        required
                                        value={formData.paymentPhone}
                                        onChange={handleChange}
                                        className="w-full h-12 bg-zinc-50 border border-transparent focus:border-black focus:bg-white rounded-xl pl-12 pr-4 text-sm font-light transition-all outline-none"
                                        placeholder="0712 345 678"
                                    />
                                </div>
                                <p className="text-[10px] text-gray-400 font-light pl-1 pt-1">Used for commission payments.</p>
                            </div>
                        </section>

                        <div className="pt-8">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full h-14 bg-black text-white font-bold rounded-xl hover:bg-zinc-900 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-widest text-xs"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        <span>Submitting Application...</span>
                                    </>
                                ) : (
                                    <>
                                        Submit Application
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>

                            <p className="text-center mt-8 text-xs text-gray-400 font-light leading-relaxed">
                                Already have an affiliate account? <br className="sm:hidden" />
                                <Link to="/login" className="text-black font-medium hover:underline ml-1">Login here</Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
